import { formatValue, lengthLimit, valuePlaceholder, showLoader, printWorkAround } from '../data-viz-helpers.js'

customElements.define('zero-amount-percent', class extends HTMLElement {
    static observedAttributes = ['data-value', 'data-label', 'data-percentage', 'data-bg', 'data-fill', 'data-help', 'data-format']

    constructor() {
        super().attachShadow({mode: 'open'})
    }

    connectedCallback() {
        this.getProps()
        this.shadowRoot.innerHTML = showLoader([this.dataset.value], this.render())
    }

    attributeChangedCallback(_, oldVal, newVal) {
        if(oldVal !== newVal) {
            this.connectedCallback()
        }
    }

    getProps() {
        this.bg = this.dataset.bg ?? 'var(--color-4-lt)'
        this.fill = this.dataset.fill ?? 'var(--color-4-dk)'
        this.format = this.dataset.format ?? ''
        this.label = this.dataset.label ?? ''
        this.value = formatValue(this.dataset.value, this.format) ?? ''
        this.percentage = parseFloat(this.dataset.percentage)
    }

    createDonutChart(percentage, { radius = 100, strokeWidth = 30, value = '' }) {
        if (percentage < 0 || percentage > 100) {
            throw new Error('Percentage must be between 0 and 100')
        }

        // Calculate path data for donut segment
        const startAngle = -90; // Start from top
        const endAngle = startAngle + (percentage / 100) * 360;
        
        const x1 = radius + (radius - strokeWidth / 2) * Math.cos(startAngle * Math.PI / 180);
        const y1 = radius + (radius - strokeWidth / 2) * Math.sin(startAngle * Math.PI / 180);
        const x2 = radius + (radius - strokeWidth / 2) * Math.cos(endAngle * Math.PI / 180);
        const y2 = radius + (radius - strokeWidth / 2) * Math.sin(endAngle * Math.PI / 180);

        // Large arc flag determines whether to draw the longer arc (1) or shorter arc (0)
        const largeArcFlag = percentage > 50 ? 1 : 0;

        const pathData = [
            `M ${x1} ${y1}`, // Move to start point
            `A ${radius - strokeWidth / 2} ${radius - strokeWidth / 2} 0 ${largeArcFlag} 1 ${x2} ${y2}` // Arc command
        ].join(' ');

        return `
            <svg 
                class="chart"
                width="${radius * 2}" 
                height="${radius * 2}" 
                viewBox="0 0 ${radius * 2} ${radius * 2}">

                <circle
                    cx="${radius}"
                    cy="${radius}"
                    r="${radius - strokeWidth / 2}"
                    fill="none"
                    stroke="${this.fill}"
                    stroke-width="${strokeWidth}"
                    style="opacity: 0.3;"></circle>

                <path 
                    d="${pathData}"
                    fill="none"
                    stroke="${this.fill}"
                    stroke-width="${strokeWidth}"
                    stroke-linecap="butt"/>

                <text 
                    x="50%"
                    y="50%"
                    alignment-baseline="central"
                    text-anchor="middle" 
                    style="fill: ${this.fill}; font-size: ${radius / 2.5}; font-weight: 800;">${value}</text>
            </svg>
        `
    }

    render() {
        return `
            <style>
                * {
                    box-sizing: border-box;
                }

                :host {
                    background-color: ${this.bg};
                    border-radius: var(--chart-radius);
                    container-type: inline-size;
                    display: grid;
                    height: 100%;
                    overflow: clip;
                    place-content: center;
                    position: relative;
                    -webkit-user-select: none;
                    user-select: none;
                }

                p {
                    all: unset;
                }

                .container {
                    align-items: center;
                    display: flex;
                    gap: 2em;
                    grid-template-columns: 1fr 1fr;
                    justify-content: space-between;
                    padding: 2em;
                }

                .amount {
                    display: grid;

                    .value {
                        color: ${this.fill};
                        font-size: 13cqw;
                        font-weight: 900;
                        text-align: left;
                    }

                    .label {
                        color: ${this.fill};
                        font-size: 4cqw;
                        font-weight: 700;
                        text-align: left;
                    }
                }

                .chart {
                    height: auto;
                    max-width: 100%;
                }
            </style>

            ${printWorkAround(this)}

            <div class="container">
                <div class="amount">
                    <p data-val class="value">${valuePlaceholder(this.value)}</p>
                    <p data-label class="label">${lengthLimit(this.label, 26)}</p>
                </div>

                ${this.createDonutChart(this.percentage * 100, {
                    value: formatValue(this.percentage, 'percent')
                })}
            </div>

            ${(this.hasAttribute('data-help')) ? `<zero-help-button msg="${this.dataset.help}" color="${this.fill.replace('-dk', '')}"></zero-help-button>` : ''}
        `
    }
})